import React, { useState, useEffect } from "react";
import {
  fetchCoachAppointmentSlots,
  saveCoachAppointmentSlots,
} from "../../apiservices/ApiService";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
  TextField,
  IconButton,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { getCoachId } from "../../common/Utils";

const CoachTimeSlotBooking = ({ data, onBack }) => {

  const fixUrlProtocol = (url) => {
    if (!url) return '';
    return url.startsWith('http:') ? url.replace(/^http:/, 'https:') : url;
  };
  const [slotData, setSlotData] = useState([]);
  const [coachData, setCoachData] = useState({});
  const todayDate = new Date().toISOString().split("T")[0];
  const [selectedDate, setSelectedDate] = useState(todayDate);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(0);
  const [appointmentReason, setAppointmentReason] = useState("");
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [filteredData, setFilteredData] = useState([]);
  const [dates, setDates] = useState([]);
  const [selected, setSelectedIndex] = useState(0);
  // const currentDate = new Date().toISOString().split("T")[0]; // Get current date in "YYYY-MM-DD" format
  function formatDate(dateString) {
    const date = new Date(dateString);
    const options = { day: "2-digit", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-GB", options); // This will format the date like "19 Dec 2024"
  }
  function modifyDate(dateString, modifier) {
    // Convert the dateString to a Date object
    let date = new Date(dateString);

    // Modify the date by adding the modifier to the current date
    date.setDate(date.getDate() + modifier);

    // Return the modified date in YYYY-MM-DD format
    return date.toISOString().split("T")[0];
  }
  // Get today's date and tomorrow's date
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  // Function to generate the required date array
  function generateDateArray(data) {
    const result = [];
    data.forEach((item) => {
      const date = new Date(item.date);
      if (date.toDateString() === today.toDateString()) {
        result.push("Today");
      } else if (date.toDateString() === tomorrow.toDateString()) {
        result.push("Tomorrow");
      } else if (date <= new Date("2024-12-26")) {
        result.push(formatDate(item.date));
      }
    });
    return result;
  }

  // const filteredData = slotData.filter((dataItem) => dataItem.date === currentDate);
  const fetchSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const params = { goqiiCoachId: 12345, queryDate: date };
      const data = await fetchCoachAppointmentSlots(params);

      const slots = data.slots;
      if (data && slots) {
        setSlotData(slots);
        setCoachData(data);
        const newdates = generateDateArray(slots); // Call generateDateArray after setting slotData
        setDates(newdates);

        // Wait for slotData to be updated before filtering
        const filteredDataq = slots.filter((dataItem) => {
          return dataItem.date === selectedDate;
        });
        setFilteredData(filteredDataq);
      } else {
        console.warn("No slot data found in the response");
        setSlotData([]);
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
      setSlotData([]);
    } finally {
      setLoadingSlots(false);
    }
  };


  const handleConfirm = async () => {
    if (!selectedSlot) {
      setSnackbar({
        open: true,
        message: "Please select a slot and provide a reason.",
        severity: "warning",
      });
      return;
    }

    setLoadingConfirm(true);
    try {
      const payload = {
        appointmentDate: selectedDate,
        appointmentSlot: selectedSlot,
        goqiiCoachId: getCoachId(),
        selectedSlot: 1,
        callType: "review",
      };

      const response = await saveCoachAppointmentSlots(payload);
      setSnackbar({
        open: true,
        message: "Appointment confirmed successfully!",
        severity: "success",
      });

      // Add a small delay to allow the Snackbar to display before navigating back
      setTimeout(() => {
        onBack();
      }, 1500); // Adjust the timeout duration as needed
    } catch (error) {
      console.error("Error saving appointment:", error);
      setSnackbar({
        open: true,
        message: "Failed to confirm appointment. Please try again.",
        severity: "error",
      });
    } finally {
      setLoadingConfirm(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    if (!selectedDate) {
      const today = new Date().toISOString().split("T")[0];
      setSelectedDate(today);
      fetchSlots(today);
    } else {
      fetchSlots(selectedDate);
    }
  }, [selectedDate, currentWeek]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    fetchSlots(date);
    setSelectedSlot("");
  };

  const handleSlotSelection = (slot) => {
    setSelectedSlot(slot);
  };

  const getDynamicLabel = (date) => {
    const today = new Date().toISOString().split("T")[0];
    const yesterday = new Date(new Date().setDate(new Date().getDate() - 1))
      .toISOString()
      .split("T")[0];
    const tomorrow = new Date(new Date().setDate(new Date().getDate() + 1))
      .toISOString()
      .split("T")[0];

    if (date === today) return "TODAY";
    if (date === yesterday) return "YESTERDAY";
    if (date === tomorrow) return "TOMORROW";

    const formattedDate = new Date(date).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return formattedDate;
  };

  const generateWeekDates = () => {
    const days = [];
    const startOfWeek = new Date();

    // Adjust to start from Monday
    const dayOfWeek = startOfWeek.getDay(); // 0 (Sunday) to 6 (Saturday)
    const offset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Offset to move to Monday

    startOfWeek.setDate(startOfWeek.getDate() + offset + currentWeek * 7); // Move to Monday of the current week

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push(date.toISOString().split("T")[0]);
    }

    return days;
  };


  const handleNextWeek = () => {
    setCurrentWeek((prev) => prev + 1);
    const newDD = modifyDate(selectedDate, 1);
    setSelectedDate(newDD);
  };

  const handlePreviousWeek = () => {
    if (currentWeek > 0) {
      setCurrentWeek((prev) => prev - 1);

      const newDD = modifyDate(selectedDate, -1);
      setSelectedDate(newDD);
    }
  };


  return (
    <Paper
      elevation={3}
      sx={{ padding: 0, maxWidth: 600, margin: "0 auto", borderRadius: 3, backgroundColor: "rgba(255, 152, 0, 0.11)", color: "#771615" }}
    >
      {/* Header Section with Back Button */}
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: 2, background: "linear-gradient(90deg, #fff 0%, rgba(255, 152, 0, 0.11) 70%)", color: "#771615" }}>
        <IconButton onClick={onBack}>
          <ArrowBackIosNewIcon color="#771615" />
        </IconButton>
        <Typography variant="h6" align="center" sx={{ flexGrow: 1 }}>
          {data?.title || "Book Coach Call"}
        </Typography>
      </Box>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        {/* Coach Image */}
        <img
          src={fixUrlProtocol(coachData.coachImageUrl)}
          alt="Coach"
          style={{
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />

        {/* Screen Title */}
        <p
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            margin: "10px 0",
            textAlign: "center",
          }}
        >
          {coachData.screenTitle}
        </p>

        {/* Call Title */}
        <h2
          style={{
            fontSize: "20px",
            fontWeight: "600",
            margin: "5px 0",
            textAlign: "center",
          }}
        >
          {coachData.callTitle}
        </h2>

        {/* Description */}
        <p
          style={{
            fontSize: "16px",
            color: "#666",
            textAlign: "center",
            margin: "0 10px",
          }}
        >
          {coachData.description}
        </p>
      </div>

      {/* Week Navigation */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <IconButton onClick={handlePreviousWeek} disabled={currentWeek === 0}>
          <ArrowBackIosNewIcon fontSize="small" color="#771615 !important" />
        </IconButton>
        {/* Calendar Icon and Dynamic Label */}
        <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: 12 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                marginRight: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontSize: 14 }}>
                {getDynamicLabel(slotData[currentWeek]?.date)}
                {/* {slotData.map((date, index)=>{
                    getDynamicLabel(date.date)
                })} */}
              </Typography>
            </Box>
          </Box>
        </Typography>
        <IconButton
          onClick={handleNextWeek}
          disabled={slotData.length - 1 === currentWeek}
        >
          <ArrowForwardIosIcon fontSize="small" color="#771615 !important" />
        </IconButton>
      </Box>

      {/* Date Picker Row */}
      {/* <Box sx={{ display: "flex", justifyContent: "space-between", overflowX: "auto", gap: 1, marginBottom: 1 }}>
        {generateWeekDates().map((date, index) => (
          <div
            key={index}
            onClick={() => handleDateChange(date)}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: 10, color: "gray", display: "flex", justifyContent: "center" }}
            >
              {new Date(date).toLocaleDateString("en-US", { weekday: "short" }).charAt(0)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: 12 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  border: "1px solid gray",
                  cursor: "pointer",
                  backgroundColor: selectedDate === date ? "black" : "",
                  color: selectedDate === date ? "white" : "black",
                }}
              >
                {new Date(date).getDate()}
              </Box>
            </Typography>
          </div>
        ))}
      </Box> */}

      {/* Time Slots */}
      <Box>
        {loadingSlots ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 80,
            }}
          >
            <CircularProgress size={20} />
          </Box>
        ) : filteredData.length > 0 ? (
          filteredData.map((dataItem, index) => (
            <Grid container spacing={1} key={index} sx={{ marginBottom: 2 }}>
              {dataItem.slots.map((slot, slotIndex) => (
                <Grid item xs={6} sm={4} key={slotIndex}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => handleSlotSelection(slot)}
                    fullWidth
                    sx={{
                      borderRadius: 5,
                      boxShadow: selectedSlot === slot ? 3 : 0,
                      textTransform: "none",
                      fontSize: 10,
                      color: selectedSlot === slot ? "orange" : "gray",
                      borderColor: "gray",
                    }}
                  >
                    {slot}
                  </Button>
                </Grid>
              ))}
            </Grid>
          ))
        ) : (
          <Typography color="error" align="center" sx={{ fontSize: 12 }}>
            No slots available for this date.
          </Typography>
        )}
      </Box>

      {/* Footer Section */}
      <Box sx={{ marginTop: 3 }}>
        <Button
          variant="contained"
          color="success"
          disabled={!selectedSlot || loadingConfirm}
          fullWidth
          onClick={handleConfirm}
          sx={{
            background: "linear-gradient(90deg, #771615 0%, #FF9800 100%)",
            color: "white !important",
          }}
        >
          {loadingConfirm ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Book Appointment"
          )}
        </Button>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default CoachTimeSlotBooking;
